
import * as React from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
    attribute?: string;
    enableSystem?: boolean;
    disableTransitionOnChange?: boolean;
};

type ThemeProviderState = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
    theme: "system",
    setTheme: () => null,
};

const ThemeProviderContext =
    React.createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "vite-ui-theme",
    attribute = "class",
    enableSystem = true,
    disableTransitionOnChange = false,
}: ThemeProviderProps) {
    const getInitialTheme = React.useCallback((): Theme => {
        if (typeof window === "undefined") return defaultTheme;
        const stored = localStorage.getItem(storageKey);
        if (stored === "light" || stored === "dark" || stored === "system") {
            return stored;
        }
        return defaultTheme;
    }, [defaultTheme, storageKey]);

    const [theme, setTheme] = React.useState<Theme>(
        getInitialTheme,
    );

    const disableTransitions = React.useCallback(() => {
        if (!disableTransitionOnChange) return () => {};

        const style = document.createElement("style");
        style.appendChild(
            document.createTextNode(
                "*{transition:none!important;-webkit-transition:none!important}",
            ),
        );
        document.head.appendChild(style);

        return () => {
            // Ensure at least one frame passes before removing.
            requestAnimationFrame(() => {
                style.remove();
            });
        };
    }, [disableTransitionOnChange]);

    const applyTheme = React.useCallback(
        (nextTheme: Theme) => {
            const root = window.document.documentElement;
            const cleanupTransitions = disableTransitions();

            if (attribute === "class") {
                root.classList.remove("light", "dark");
            } else {
                root.removeAttribute(attribute);
            }

            const resolvedTheme =
                nextTheme === "system" && enableSystem
                    ? window.matchMedia("(prefers-color-scheme: dark)").matches
                        ? "dark"
                        : "light"
                    : nextTheme;

            if (attribute === "class") {
                root.classList.add(resolvedTheme);
            } else {
                root.setAttribute(attribute, resolvedTheme);
            }

            // Keep native controls coherent with current theme.
            root.style.colorScheme = resolvedTheme;
            cleanupTransitions();
        },
        [attribute, disableTransitions, enableSystem],
    );

    React.useEffect(() => {
        applyTheme(theme);
    }, [theme, applyTheme]);

    React.useEffect(() => {
        if (!enableSystem || theme !== "system") return;

        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => applyTheme("system");

        if (typeof media.addEventListener === "function") {
            media.addEventListener("change", onChange);
            return () => media.removeEventListener("change", onChange);
        }

        media.addListener(onChange);
        return () => media.removeListener(onChange);
    }, [enableSystem, theme, applyTheme]);

    const value = {
        theme,
        setTheme: (theme: Theme) => {
            localStorage.setItem(storageKey, theme);
            setTheme(theme);
        },
    };

    return (
        <ThemeProviderContext.Provider value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const useTheme = () => {
    const context = React.useContext(ThemeProviderContext);

    if (context === undefined)
        throw new Error("useTheme must be used within a ThemeProvider");

    return context;
};
